import { useCallback, useMemo, useRef, useState } from 'react';

import { useNotifications } from '@decisiv/ui-components';

import {
  fetchAccountAccess,
  removeAccess,
  restoreAccess,
} from '../services/usersService';
import { ACCESS_MODAL } from '../components/accessCopy';

const NOTIFICATION_DURATION = 8000;

export const REMOVE = 'remove';
export const RESTORE = 'restore';

const errorMessage = (errors) =>
  (errors?.base || []).join(' ') || ACCESS_MODAL.generic;

/**
 * The Users list's Remove Access / Restore Access modals (PD-4677).
 *
 * The account list is fetched when the modal opens rather than carried on the
 * row: the row's ACCOUNTS cell holds names for display, and these modals need
 * ids to submit and the removed/active split to know which half to offer. One
 * request per open is the cost of not widening every row in the table.
 *
 * Nothing is preselected. Preselecting every account made "remove all access"
 * the default and inverted the tags' x: clicking one spared that account
 * instead of removing it, so unticking three of eighteen removed the other
 * fifteen (reported in QA on PD-4677). A destructive action does not get a
 * destructive default, and the field's own help already says to choose from the
 * list. Removing every account is still one action — select them all — which is
 * what the ticket asks for.
 *
 * Each open takes a ticket from `requestId` and only writes state while it is
 * still the latest. The modal stays mounted between openings, so without it a
 * slow fetch for one user lands on the dialog a later open has already painted
 * for another: the title names the second user while the list offers the
 * first's accounts, and submitting revokes access against a user the admin
 * never chose it for. `close` takes a ticket too, so a response arriving after
 * Cancel writes nothing.
 */
function useUserAccess({ onChanged }) {
  const [action, setAction] = useState(null);
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const requestId = useRef(0);

  const { notify } = useNotifications();

  const open = useCallback(async (nextAction, row) => {
    requestId.current += 1;
    const request = requestId.current;

    setAction(nextAction);
    setUser(row);
    setAccounts([]);
    setSelectedIds([]);
    setError(null);
    setIsLoading(true);

    try {
      const rows = await fetchAccountAccess(row.id);

      if (request !== requestId.current) return;

      // Remove offers what they still hold; restore offers what was taken away.
      const offered = rows.filter((account) =>
        nextAction === REMOVE ? !account.removedAt : account.removedAt,
      );

      setAccounts(offered);
      setSelectedIds([]);
    } catch (err) {
      if (request !== requestId.current) return;

      setError(ACCESS_MODAL.generic);
    } finally {
      if (request === requestId.current) setIsLoading(false);
    }
  }, []);

  const close = useCallback(() => {
    requestId.current += 1;

    setAction(null);
    setUser(null);
    setAccounts([]);
    setSelectedIds([]);
    setError(null);
  }, []);

  const submit = useCallback(async () => {
    if (!user || !action || !selectedIds.length) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const act = action === REMOVE ? removeAccess : restoreAccess;
      const result = await act(user.id, selectedIds);

      if (result.errors) {
        setError(errorMessage(result.errors));

        return;
      }

      const copy = ACCESS_MODAL[action];
      notify({
        intent: 'success',
        title: copy.toastTitle,
        duration: NOTIFICATION_DURATION,
        onClose: () => {},
      });
      close();
      // Refetches the list: a user who lost their last account has moved to the
      // other tab, and one who lost some of them has different cells now.
      onChanged();
    } catch (err) {
      setError(ACCESS_MODAL.generic);
    } finally {
      setIsSubmitting(false);
    }
  }, [action, close, notify, onChanged, selectedIds, user]);

  const accountOptions = useMemo(
    () =>
      accounts.map((account) => ({ label: account.name, value: account.id })),
    [accounts],
  );

  return {
    accountOptions,
    action,
    close,
    error,
    isLoading,
    isOpen: Boolean(action),
    isSubmitting,
    open,
    selectedIds,
    setSelectedIds,
    submit,
    user,
  };
}

export default useUserAccess;
